
.page {
  min-height: 100vh;
  display: flex;
  background: var(--color-background-primary);
}

// Theme Toggle
.themeToggle {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface-primary);
  border: 2px solid var(--color-border);
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1001;

  @include transition(transform, box-shadow, border-color);

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    border-color: var(--color-primary-base);
  }
}

// Sidebar
.sidebar {
  width: 280px;
  background: var(--color-surface-primary);
  border-right: 1px solid var(--color-border);
  padding: map.get($spacing-scale, 'lg');
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  overflow-y: auto;
}

.sidebarHeader {
  margin-bottom: map.get($spacing-scale, 'xl');
  padding-bottom: map.get($spacing-scale, 'lg');
  border-bottom: 1px solid var(--color-border);
}

.sidebarTitle {
  @include heading-4;
  color: var(--color-text-primary);
  margin: 0 0 map.get($spacing-scale, 'xs') 0;
}

.sidebarSubtitle {
  @include body-small-regular;
  color: var(--color-text-secondary);
  margin: 0;
}

.sidebarNav {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'xs');
}

.sidebarGroup {
  display: flex;
  flex-direction: column;
}

.sidebarGroupLabel {
  @include metadata-medium;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: map.get($spacing-scale, 'xs') map.get($spacing-scale, 'md');
  margin-bottom: map.get($spacing-scale, 'xs');
}

.sidebarGroupItems {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sidebarSeparator {
  height: 1px;
  background-color: var(--color-border);
  margin: map.get($spacing-scale, 'sm') map.get($spacing-scale, 'md');
}

.sidebarLink {
  display: block;
  width: 100%;
  padding: map.get($spacing-scale, 'sm') map.get($spacing-scale, 'md');
  background: transparent;
  border: none;
  border-radius: map.get($radius, 'md');
  text-align: left;
  cursor: pointer;
  @include body-regular;
  color: var(--color-text-secondary);

  @include transition(background-color, color);

  &:hover {
    background: var(--color-surface-alt);
    color: var(--color-text-primary);
  }
}

.sidebarLinkActive {
  background: rgba(95, 91, 243, 0.08);
  color: var(--color-primary-base);

  &:hover {
    background: rgba(95, 91, 243, 0.12);
  }
}

// Main Content
.main {
  flex: 1;
  margin-left: 280px;
  padding: map.get($spacing-scale, 'xl');
  max-width: 1000px;
}

// Sections
.section {
  margin-bottom: map.get($spacing-scale, '2xl');
}

.sectionHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: map.get($spacing-scale, 'md');
  margin-bottom: map.get($spacing-scale, 'sm');
}

.sectionTitle {
  @include heading-3;
  color: var(--color-text-primary);
  margin: 0;
}

.figmaLink {
  display: inline-flex;
  align-items: center;
  gap: map.get($spacing-scale, 'xs');
  padding: 8px 14px;
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'full');
  text-decoration: none;
  @include caption-medium;
  color: var(--color-text-secondary);
  white-space: nowrap;

  @include transition(background-color, border-color, color, transform);

  &:hover {
    background: var(--color-surface-primary);
    border-color: var(--color-primary-base);
    color: var(--color-text-primary);
    transform: translateY(-1px);
  }

  svg {
    flex-shrink: 0;
  }
}

.sectionDesc {
  @include body-regular;
  color: var(--color-text-secondary);
  margin: 0 0 map.get($spacing-scale, 'xl') 0;
}

// Subsections
.subsection {
  margin-bottom: map.get($spacing-scale, 'xl');
}

.subsectionTitle {
  @include body-medium;
  color: var(--color-text-primary);
  margin: 0 0 map.get($spacing-scale, 'md') 0;
}

// Rows
.row {
  display: flex;
  flex-wrap: wrap;
  gap: map.get($spacing-scale, 'md');
  align-items: center;
}

.inputRow {
  max-width: 400px;
}

// Tab Content
.tabContent {
  margin-top: map.get($spacing-scale, 'md');
  padding: map.get($spacing-scale, 'md');
  background: var(--color-surface-alt);
  border-radius: map.get($radius, 'md');

  p {
    @include body-regular;
    color: var(--color-text-secondary);
    margin: 0;
  }
}

// Icon Grids
.iconGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: map.get($spacing-scale, 'md');
}

.iconItem {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: map.get($spacing-scale, 'xs');
  padding: map.get($spacing-scale, 'md');
  background: var(--color-surface-alt);
  border-radius: map.get($radius, 'md');

  span {
    @include metadata-regular;
    color: var(--color-text-secondary);
  }

  svg {
    color: var(--color-text-primary);
  }
}

.iconGridLarge {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: map.get($spacing-scale, 'md');
}

.iconItemLarge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: map.get($spacing-scale, 'sm');
  padding: map.get($spacing-scale, 'lg');
  background: var(--color-surface-alt);
  border-radius: map.get($radius, 'md');

  span {
    @include body-small-regular;
    color: var(--color-text-secondary);
  }
}

// Empty State Box
.emptyStateBox {
  padding: map.get($spacing-scale, 'xl');
  background: var(--color-surface-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'lg');
  display: flex;
  justify-content: center;
}

// Header Box
.headerBox {
  background: var(--color-background-primary);
  padding: map.get($spacing-scale, 'md');
  border-radius: map.get($radius, 'md');
}

// Lead Type Header Box
.leadTypeHeaderBox {
  background: var(--color-surface-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'lg');
  overflow: hidden;
}

// Logo Box
.logoBox {
  display: inline-flex;
  padding: map.get($spacing-scale, 'lg');
  background: var(--color-surface-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'md');
}

.logoBoxDark {
  display: inline-flex;
  padding: map.get($spacing-scale, 'lg');
  background: var(--color-neutrals-black);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'md');
}

// List Item Container
.listItemContainer {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'xs');
  max-width: 400px;
  padding: map.get($spacing-scale, 'sm');
  background: var(--color-surface-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'md');
}

// Menu Select Container
.menuSelectContainer {
  max-width: 400px;
}

// Profile Card Container
.profileCardContainer {
  max-width: 468px;
}

// Profile Card Grid (for state comparisons)
.profileCardGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: map.get($spacing-scale, 'lg');
  max-width: 100%;
}

.cardLabel {
  @include metadata-medium;
  color: var(--color-text-secondary);
  margin: 0 0 map.get($spacing-scale, 's-2') 0;
}

// Selectable Row Container
.selectableRowContainer {
  max-width: 400px;
  background: var(--color-surface-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'md');
  overflow: hidden;
}

// Table Cells Container
.tableCellsContainer {
  max-width: 400px;
  background: var(--color-surface-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'md');
  overflow: hidden;
}

.tableRow {
  display: flex;
  border-bottom: 1px solid var(--color-grey-soft);

  &:last-child {
    border-bottom: none;
  }
}

.tableCellPrimary {
  flex: 1;
  padding: 0 map.get($spacing-scale, 'md');
}

.tableCellSecondary {
  width: 80px;
  padding: 0 map.get($spacing-scale, 'md');
  text-align: right;
}

// Contact Link Container
.contactLinkContainer {
  max-width: 200px;
}

.contactLinkGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: map.get($spacing-scale, 'md');
  max-width: 600px;
}

// TypeAhead Container
.typeAheadContainer {
  max-width: 440px;
}

// Select Filter Container
.selectFilterContainer {
  position: relative;
  min-height: 300px;
}

.selectFilterStateDemo {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'sm');
  align-items: flex-start;
}

.stateLabel {
  @include metadata-medium;
  color: var(--color-text-secondary);
}

// Header Demo Box
.headerDemoBox {
  background: var(--color-background-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'md');
  overflow: hidden;
}

.headerDemoBoxDark {
  background: #1a1a1a;
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'md');
  overflow: hidden;
}

.hint {
  @include body-small-regular;
  color: var(--color-text-tertiary);
  font-style: italic;
  margin-top: map.get($spacing-scale, 'sm');
}

.featureList {
  @include body-small-regular;
  color: var(--color-text-secondary);
  margin: 0;
  padding-left: map.get($spacing-scale, 'lg');
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'xs');

  li {
    line-height: 1.6;
  }
}

// Modal Backdrop
.modalBackdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

// Pages Section
.pagesGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: map.get($spacing-scale, 'lg');
  margin-bottom: map.get($spacing-scale, 'xl');
}

.pageCard {
  background: var(--color-surface-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'lg');
  padding: map.get($spacing-scale, 'lg');
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'md');

  @include transition(border-color, box-shadow);

  &:hover {
    border-color: var(--color-primary-base);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.pageCardHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pageCardTitle {
  @include heading-4;
  color: var(--color-text-primary);
  margin: 0;
}

.pageCardLink {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--color-primary-base);
  color: var(--color-primary-on-primary);
  border-radius: map.get($radius, 'md');
  text-decoration: none;

  @include transition(background-color, transform);

  &:hover {
    background: var(--color-primary-dark);
    transform: translateX(2px);
  }
}

.pageCardDescription {
  @include body-regular;
  color: var(--color-text-secondary);
  margin: 0;
}

.pageCardMeta {
  display: flex;
  flex-wrap: wrap;
  gap: map.get($spacing-scale, 'md');
}

.pageCardMetaItem {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, '2xs');
}

.pageCardMetaLabel {
  @include metadata-medium;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.pageCardMetaValue {
  @include body-small-regular;
  color: var(--color-primary-base);
  background: var(--color-primary-opacity-08);
  padding: 4px 8px;
  border-radius: map.get($radius, 'sm');
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
}

.pageCardStates {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'xs');
  padding-top: map.get($spacing-scale, 'sm');
  border-top: 1px solid var(--color-border);
}

.pageCardStatesLabel {
  @include metadata-medium;
  color: var(--color-text-tertiary);
}

.pageCardStatesList {
  display: flex;
  flex-wrap: wrap;
  gap: map.get($spacing-scale, 'xs');
}

.pageCardState {
  @include caption-regular;
  color: var(--color-text-secondary);
  background: var(--color-surface-alt);
  padding: 4px 10px;
  border-radius: map.get($radius, 'full');
}

.pageCardStateLink {
  @include caption-medium;
  color: var(--color-primary-base);
  background: var(--color-primary-opacity-08);
  padding: 6px 12px;
  border-radius: map.get($radius, 'full');
  text-decoration: none;
  cursor: pointer;

  @include transition(background-color, color, transform);

  &:hover {
    background: var(--color-primary-base);
    color: var(--color-primary-on-primary);
    transform: translateY(-1px);
  }
}

.futurePagesGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: map.get($spacing-scale, 'md');
  margin-top: map.get($spacing-scale, 'md');
}

.futurePageCard {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'xs');
  padding: map.get($spacing-scale, 'md');
  background: var(--color-surface-alt);
  border-radius: map.get($radius, 'md');
  border: 1px dashed var(--color-border);
  opacity: 0.7;
}

.futurePageName {
  @include body-medium;
  color: var(--color-text-secondary);
}

.futurePagePath {
  @include metadata-regular;
  color: var(--color-text-tertiary);
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
}

// Typography Section
.typographyList {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'lg');
}

.typographyItem {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'xs');
  padding: map.get($spacing-scale, 'md');
  background: var(--color-surface-alt);
  border-radius: map.get($radius, 'md');
}

.typographyLabel {
  @include metadata-medium;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.typographyCode {
  @include metadata-regular;
  color: var(--color-primary-base);
  background: var(--color-primary-opacity-08);
  padding: 4px 8px;
  border-radius: map.get($radius, 'sm');
  width: fit-content;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
}

// Typography Styles
.display1 {
  @include display-1;
  color: var(--color-text-primary);
}

.heading1 {
  @include heading-1;
  color: var(--color-text-primary);
}

.heading2 {
  @include heading-2;
  color: var(--color-text-primary);
}

.heading3 {
  @include heading-3;
  color: var(--color-text-primary);
}

.heading4 {
  @include heading-4;
  color: var(--color-text-primary);
}

.bodyLargeRegular {
  @include body-large-regular;
  color: var(--color-text-primary);
}

.bodyLargeMedium {
  @include body-large-medium;
  color: var(--color-text-primary);
}

.bodyLargeBold {
  @include body-large-bold;
  color: var(--color-text-primary);
}

.bodyRegular {
  @include body-regular;
  color: var(--color-text-primary);
}

.bodyMedium {
  @include body-medium;
  color: var(--color-text-primary);
}

.bodyBold {
  @include body-bold;
  color: var(--color-text-primary);
}

.bodySmallRegular {
  @include body-small-regular;
  color: var(--color-text-primary);
}

.bodySmallMedium {
  @include body-small-medium;
  color: var(--color-text-primary);
}

.bodySmallBold {
  @include body-small-bold;
  color: var(--color-text-primary);
}

.captionRegular {
  @include caption-regular;
  color: var(--color-text-primary);
}

.captionMedium {
  @include caption-medium;
  color: var(--color-text-primary);
}

.metadataRegular {
  @include metadata-regular;
  color: var(--color-text-primary);
}

.metadataMedium {
  @include metadata-medium;
  color: var(--color-text-primary);
}

.metadataSmallRegular {
  @include metadata-small-regular;
  color: var(--color-text-primary);
}

.metadataSmallMedium {
  @include metadata-small-medium;
  color: var(--color-text-primary);
}

// Colors Section
.colorGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: map.get($spacing-scale, 'md');
}

.colorItem {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'xs');
}

.colorSwatch {
  width: 100%;
  height: 64px;
  border-radius: map.get($radius, 'md');
}

.colorSwatchWithBorder {
  width: 100%;
  height: 64px;
  border-radius: map.get($radius, 'md');
  border: 1px solid var(--color-border);
}

.colorName {
  @include body-small-medium;
  color: var(--color-text-primary);
}

.colorCode {
  @include metadata-regular;
  color: var(--color-text-secondary);
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
}

// SSO Buttons Demo
.ssoButtonsDemo {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'md');
  max-width: 400px;
}

// OTP Demo
.otpDemo {
  max-width: 400px;
}

// Login Page Preview
.loginPagePreview {
  height: 600px;
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'lg');
  overflow: hidden;
  position: relative;
}

// Feature List
.featureList {
  margin: map.get($spacing-scale, 'sm') 0;
  padding-left: map.get($spacing-scale, 'lg');

  @include body-small-regular;
  color: var(--color-text-secondary);

  li {
    margin-bottom: map.get($spacing-scale, 'xs');
  }
}

// Props Table
.propsTable {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'sm');
  background-color: var(--color-surface-alt);
  border-radius: map.get($radius, 'md');
  padding: map.get($spacing-scale, 'md');
}

.propRow {
  display: flex;
  gap: map.get($spacing-scale, 'md');
  align-items: baseline;

  code {
    flex-shrink: 0;
    padding: map.get($spacing-scale, 'xs') map.get($spacing-scale, 'sm');
    background-color: var(--color-background-primary);
    border-radius: map.get($radius, 'sm');
    font-family: $font-family-mono;

    @include caption-medium;
    color: var(--color-primary-base);
  }

  span {
    @include body-small-regular;
    color: var(--color-text-secondary);
  }
}

// Responsive
@media (max-width: $breakpoint-md) {
  .sidebar {
    position: static;
    width: 100%;
    height: auto;
    border-right: none;
    border-bottom: 1px solid var(--color-border);
  }

  .main {
    margin-left: 0;
  }

  .page {
    flex-direction: column;
  }
}

// Icon Button Demo
.iconButtonDemo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: map.get($spacing-scale, 'xs');
}

.iconButtonLabel {
  @include caption-regular;
  color: var(--color-text-secondary);
}

// Platform Icon Demo
.platformIconDemo {
  display: flex;
  flex-wrap: wrap;
  gap: map.get($spacing-scale, 'lg');
  align-items: center;
}

.platformIconItem {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: map.get($spacing-scale, 'sm');
  padding: map.get($spacing-scale, 'md');
  background: var(--color-surface-alt);
  border-radius: map.get($radius, 'md');
  min-width: 100px;

  span {
    @include caption-regular;
    color: var(--color-text-secondary);
    text-transform: capitalize;
  }
}

// Info Chip Row
.infoChipRow {
  display: flex;
  flex-wrap: wrap;
  gap: map.get($spacing-scale, 'md');
  align-items: center;
}

// Avatar Demo
.avatarRow {
  display: flex;
  flex-wrap: wrap;
  gap: map.get($spacing-scale, 'md');
  align-items: center;
}

.avatarDemo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: map.get($spacing-scale, 'sm');
  padding: map.get($spacing-scale, 'md');
  background: var(--color-surface-alt);
  border-radius: map.get($radius, 'md');
  min-width: 80px;
}

.avatarLabel {
  @include caption-regular;
  color: var(--color-text-secondary);
}

// Social Icon Demo
.socialIconDemo {
  display: flex;
  flex-wrap: wrap;
  gap: map.get($spacing-scale, 'lg');
  align-items: center;
}

.socialIconItem {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: map.get($spacing-scale, 'sm');
  padding: map.get($spacing-scale, 'md');
  background: var(--color-surface-alt);
  border-radius: map.get($radius, 'md');
  min-width: 80px;

  span {
    @include caption-regular;
    color: var(--color-text-secondary);
  }
}

// WhatsApp Card Demo
.whatsappCardContainer {
  max-width: 468px;
}

.whatsappCardGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: map.get($spacing-scale, 'lg');
}
